import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Resizable } from "re-resizable";
import { Tooltip, Typography } from "@mui/material";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import useStore from "../../../../Store/Store";
import TextEditorWithJSONViewer from "../../../TextEditor/TextEditorWithJSONViewer";
import DataExampleButtons from "./DataExampleButtons";
import { theme } from "../../../../Themes/Themes";
import { useResponsiveValue } from "../../../../utils/responsive";
import {
  floatingStepDescriptionConfig,
  sourceDocConfig,
} from "../../../../utils/responsive/componentConfig";

interface FloatingStepDescriptionWindowProps {
  isVisible: boolean;
  onClose: () => void;
}

export const FloatingStepDescriptionWindow: React.FC<
  FloatingStepDescriptionWindowProps
> = ({ isVisible, onClose }) => {
  // Get responsive configuration
  const responsiveConfig = useResponsiveValue(floatingStepDescriptionConfig);
  const sourceDocResponsiveConfig = useResponsiveValue(sourceDocConfig);

  // Get data directly from store instead of context
  const {
    currentUnsavedSimulationObject,
    currentStep,
    isEditingSimulations,
    setCurrentUnsavedSimulationObject,
  } = useStore((state) => ({
    currentUnsavedSimulationObject: state.currentUnsavedSimulationObject,
    currentStep: state.simulationsState.currentStep,
    isEditingSimulations: state.simulationsState.isEditingSimulations,
    setCurrentUnsavedSimulationObject: state.setCurrentUnsavedSimulationObject,
  }));

  const [simStepDescription, setSimStepDescription] = useState("");
  const [shouldRenderEditor, setShouldRenderEditor] = useState(false);
  const [width, setWidth] = useState(responsiveConfig.width);
  const [height, setHeight] = useState(150);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [editorFontSize, setEditorFontSize] = useState(115); // Default 115% (15% larger)

  const usingWikiAsSimStepDescription = useRef(false);
  const windowRef = useRef<HTMLDivElement>(null);
  const hasInitializedPosition = useRef(false);

  // Update width when responsive config changes
  useEffect(() => {
    setWidth(responsiveConfig.width);
  }, [responsiveConfig.width]);

  // Calculate position using responsive configuration
  const calculatePosition = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Calculate actual SourceDoc left edge from SourceDoc config
    const sourceDocWidthPercent =
      parseFloat(sourceDocResponsiveConfig.width.replace("%", "")) / 100;
    const sourceDocRightPixels = parseFloat(
      sourceDocResponsiveConfig.right.replace("px", "")
    );

    // SourceDoc left edge = screenWidth - sourceDocWidth - rightOffset
    const sourceDocWidth = screenWidth * sourceDocWidthPercent;
    const sourceDocLeftEdge =
      screenWidth - sourceDocWidth - sourceDocRightPixels;

    const initialX = sourceDocLeftEdge - width - responsiveConfig.gap;
    const initialY = screenHeight - height - 10; // 10px from bottom

    return { x: Math.max(10, initialX), y: initialY };
  };

  // Initialize position so right edge touches left edge of source doc
  useEffect(() => {
    if (isVisible && !hasInitializedPosition.current) {
      // Only set initial position once
      const newPosition = calculatePosition();
      setPosition(newPosition);
      hasInitializedPosition.current = true;
    }
  }, [isVisible, width, height, responsiveConfig]);

  // Update position when responsive config changes (but keep the hasInitializedPosition flag)
  useEffect(() => {
    if (isVisible && hasInitializedPosition.current) {
      const newPosition = calculatePosition();
      setPosition(newPosition);
    }
  }, [
    responsiveConfig.gap,
    width,
    sourceDocResponsiveConfig.width,
    sourceDocResponsiveConfig.right,
  ]);

  // Handle mouse events for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        // Constrain to screen bounds
        const maxX = window.innerWidth - width;
        const maxY = window.innerHeight - height;

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, width, height]);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent default to avoid text selection
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleFontSizeChange = (fontSize: number) => {
    setEditorFontSize(fontSize);
  };

  const handleSimStepDescriptionChange = async (data: string) => {
    if (!isEditingSimulations || !currentUnsavedSimulationObject?.simSteps) {
      return;
    }
    setSimStepDescription(data);
    await setCurrentUnsavedSimulationObject({
      ...currentUnsavedSimulationObject,
      simSteps: currentUnsavedSimulationObject.simSteps.map((step) => {
        if (step.simStepId === currentStep) {
          return { ...step, simStepDescription: data };
        }
        return step;
      }),
    });
  };

  // Sets the simStepDescription state to the current step's description
  useEffect(() => {
    if (!currentUnsavedSimulationObject?.simSteps) {
      setSimStepDescription("");
      usingWikiAsSimStepDescription.current = false;
      return;
    }

    const currentStepObject = currentUnsavedSimulationObject.simSteps.find(
      (step) => step.simStepId === currentStep
    );

    if (!currentStepObject) {
      setSimStepDescription("");
      usingWikiAsSimStepDescription.current = false;
      return;
    }

    let baseDescription = "";

    // Get base description from step or wiki (without appending data examples)
    const currentStepDescription = currentStepObject?.simStepDescription;
    if (currentStepDescription) {
      // Remove any existing data example sections from the description
      baseDescription = currentStepDescription
        .replace(/\n\n\*\*Input Data Example:\*\*\s*```json[\s\S]*?```/g, "")
        .replace(/\n\n\*\*Output Data Example:\*\*\s*```json[\s\S]*?```/g, "")
        .replace(/\n\n\*\*Data example:\*\*\s*```json[\s\S]*?```/g, "")
        .trim();
      usingWikiAsSimStepDescription.current = false;
    } else {
      // check if there is a wiki stored in the path of the current step's diagram node
      const currentStepDiagramNodeId = currentStepObject?.diagramNodeId;
      if (currentStepDiagramNodeId && !isEditingSimulations) {
        const cellToPath = useStore.getState().cellToPath;
        const path =
          cellToPath[currentStepDiagramNodeId] || currentStepDiagramNodeId;
        const repoDataObj = useStore.getState().repoData[path];
        if (repoDataObj?.wiki) {
          baseDescription = repoDataObj.wiki;
          usingWikiAsSimStepDescription.current = true;
        } else {
          usingWikiAsSimStepDescription.current = false;
        }
      } else {
        usingWikiAsSimStepDescription.current = false;
      }
    }

    // Use clean description without appending examples
    setSimStepDescription(baseDescription);
  }, [
    currentStep,
    currentUnsavedSimulationObject?.simSteps,
    isEditingSimulations,
  ]);

  // Get current step object for data examples
  const currentStepObject = currentUnsavedSimulationObject?.simSteps?.find(
    (step) => step.simStepId === currentStep
  );

  // Use an effect to delay editor rendering when simulation steps change
  useEffect(() => {
    setShouldRenderEditor(false);
    const timer = setTimeout(() => {
      setShouldRenderEditor(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentUnsavedSimulationObject?.simSteps?.length, currentStep]);

  const onResizeStart = () => {
    // Create a transparent overlay to prevent interference during resize
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100vw";
    overlay.style.height = "100vh";
    overlay.style.zIndex = "9999";
    overlay.style.backgroundColor = "transparent";
    overlay.style.cursor = "nw-resize";
    document.body.appendChild(overlay);

    const removeOverlay = () => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
      window.removeEventListener("mouseup", removeOverlay);
    };
    window.addEventListener("mouseup", removeOverlay);
  };

  if (!isVisible || !currentStep || !currentUnsavedSimulationObject)
    return null;

  return createPortal(
    <div
      data-testid="step-description-window"
      ref={windowRef}
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 1,
        pointerEvents: "auto",
        filter: "drop-shadow(0px 4px 12px rgba(0, 0, 0, 0.3))",
      }}
    >
      <Resizable
        size={{ width, height }}
        onResizeStart={onResizeStart}
        onResizeStop={(e, direction, ref, d) => {
          // Update size without affecting position
          const newWidth = width + d.width;
          const newHeight = height + d.height;

          // Constrain to screen bounds if needed
          const screenWidth = window.innerWidth;
          const screenHeight = window.innerHeight;
          const maxWidth = Math.min(1200, screenWidth - position.x - 20);
          const maxHeight = Math.min(800, screenHeight - position.y - 20);

          setWidth(Math.min(newWidth, maxWidth));
          setHeight(Math.min(newHeight, maxHeight));
        }}
        minWidth={400}
        minHeight={100}
        maxWidth={1200}
        maxHeight={800}
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          border: "0px solid #ffaea6",
          overflow: "visible",
        }}
      >
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Content Area - using original wikiContainer styling */}
          <div
            style={{
              flex: 1,
              position: "relative",
              overflow: "visible",
            }}
          >
            <Typography
              variant="subtitle1"
              color="primary"
              className="boxTitle"
              style={{
                backgroundColor: "white",
                position: "absolute",
                top: "-13px",
                left: "20px",
                zIndex: 1,
                padding: "1px 12px 1px",
                border: "1px solid #ffaea6",
                borderRadius: "14px",
                fontSize: "12px",
                fontWeight: 500,
                color: "#ffaea6",
              }}
            >
              Step Description
            </Typography>

            {usingWikiAsSimStepDescription.current && (
              <div
                className="wikiIndicator"
                style={{
                  position: "absolute",
                  bottom: "10px",
                  left: "15px",
                  color: "var(--grey)",
                  zIndex: 9999,
                }}
              >
                <Tooltip title="Using wiki as step description, you can add custom SimStep description in edit mode">
                  <ArticleRoundedIcon fontSize="medium" />
                </Tooltip>
              </div>
            )}

            <div
              className="wikiDiv"
              style={{
                position: "relative",
                height: "100%",
                border: "1px solid #ffaea6",
                borderRadius: "10px",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "flex-start",
                overflow: "visible",
                boxSizing: "border-box",
              }}
            >
              {/* Top drag bar area - invisible but draggable */}
              <div
                style={{
                  position: "absolute",
                  top: "0px",
                  left: "0px",
                  right: "0px",
                  height: "15px",
                  cursor: "move",
                  zIndex: 10001,
                  userSelect: "none",
                  borderRadius: "10px 10px 0 0", // Match the container's border radius
                }}
                onMouseDown={handleMouseDown}
              />
              {/* Data Example Buttons */}
              {currentStepObject && (
                <DataExampleButtons
                  inputDataExample={currentStepObject.inputDataExample}
                  outputDataExample={currentStepObject.outputDataExample}
                  isEdge={Boolean(currentStepObject.isEdge)}
                  onFontSizeChange={handleFontSizeChange}
                  disabled={isEditingSimulations === "SimSteps"}
                />
              )}

              {/* Text Editor Container with original styling */}
              <div
                className="textEditorContainer"
                style={{
                  position: "absolute",
                  top:
                    currentStepObject &&
                    (currentStepObject.inputDataExample ||
                      currentStepObject.outputDataExample)
                      ? "45px"
                      : "0px",
                  left: "0",
                  right: "0",
                  bottom: "0",
                }}
              >
                {currentStep && shouldRenderEditor ? (
                  <TextEditorWithJSONViewer
                    key={`floating-step-description-${currentStep}`}
                    content={simStepDescription}
                    disabled={!isEditingSimulations}
                    showToolbar={isEditingSimulations === "SimSteps"}
                    handleWikiChange={handleSimStepDescriptionChange}
                    isMaxSD={false}
                    customStyle={{
                      position: "absolute",
                      top: "0px",
                      fontSize: `${editorFontSize}%`,
                    }}
                    enableMarkdown={true}
                  />
                ) : currentStep ? (
                  <div
                    className="editor-loading"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "#999",
                      fontSize: "14px",
                    }}
                  >
                    Loading editor...
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Resizable>
    </div>,
    document.body
  );
};

export default FloatingStepDescriptionWindow;
